import { useTranslation } from 'react-i18next';
import type { Location, Playlist, Schedule, Screen, ScreenGroup } from '@ghostscreen/contracts';
import { QueryEmptyState } from '../../components/dashboard/QueryEmptyState';
import { daypartColorFor } from '../../lib/schedule/daypartPalette';
import { formatDaysOfWeek } from '../../lib/schedule/formatDaysOfWeek';
import { formatLocalWindow } from '../../lib/schedule/formatLocalWindow';
import {
  isWindowActiveAtMinutes,
  localTimeToMinutes,
} from '../../lib/schedule/scheduleWindow';
import { locationNameForScreen } from '../../lib/schedule/timezoneForScreen';

type ScheduleDaypartsCardProps = {
  schedules: Schedule[];
  screens: Screen[];
  groups: ScreenGroup[];
  playlists: Playlist[];
  locations: Location[];
  now: Date;
  onOpen: (scheduleId: string) => void;
};

/**
 * Dayparts roster — Ghost Screen Schedule.dc.html.
 */
export function ScheduleDaypartsCard({
  schedules,
  screens,
  groups,
  playlists,
  locations,
  now,
  onOpen,
}: ScheduleDaypartsCardProps) {
  const { t } = useTranslation('schedule');
  const sorted = [...schedules].sort((a, b) => {
    if (a.priority !== b.priority) return b.priority - a.priority;
    return a.startLocalTime.localeCompare(b.startLocalTime);
  });

  return (
    <section
      className="min-w-0 overflow-hidden rounded-[14px] border"
      style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
    >
      <div
        className="flex flex-wrap items-center gap-3 border-b px-[22px] py-[17px]"
        style={{ borderColor: 'var(--gs-line)' }}
      >
        <span className="text-base font-bold" style={{ color: 'var(--gs-ink)' }}>
          {t('dayparts.title')}
        </span>
        <span className="text-[12.5px]" style={{ color: 'var(--gs-muted)' }}>
          {t('dayparts.subtitle')}
        </span>
      </div>

      {sorted.length === 0 ? (
        <div className="px-[22px] py-5">
          <QueryEmptyState>{t('dayparts.empty')}</QueryEmptyState>
        </div>
      ) : (
        sorted.map((schedule) => (
          <DaypartRow
            key={schedule.id}
            schedule={schedule}
            screens={screens}
            groups={groups}
            playlists={playlists}
            locations={locations}
            now={now}
            onOpen={onOpen}
          />
        ))
      )}
    </section>
  );
}

function DaypartRow({
  schedule,
  screens,
  groups,
  playlists,
  locations,
  now,
  onOpen,
}: {
  schedule: Schedule;
  screens: Screen[];
  groups: ScreenGroup[];
  playlists: Playlist[];
  locations: Location[];
  now: Date;
  onOpen: (scheduleId: string) => void;
}) {
  const { t } = useTranslation('schedule');
  const isGroup = Boolean(schedule.screenGroupId);
  const screen = screens.find((item) => item.id === schedule.screenId);
  const group = groups.find((item) => item.id === schedule.screenGroupId);
  const playlist =
    playlists.find((item) => item.id === schedule.playlistId)?.name ??
    t('dayparts.unknownPlaylist');
  const locationName = isGroup ? null : locationNameForScreen(screen, locations);
  const targetLabel = isGroup
    ? (group?.name ?? t('dayparts.groupFallback'))
    : [locationName, screen?.name].filter(Boolean).join(' · ') ||
      screen?.name ||
      t('dayparts.screenFallback');
  const color = daypartColorFor(schedule.id);
  const onAir = isWindowActiveAtMinutes(
    schedule.startLocalTime,
    schedule.endLocalTime,
    schedule.daysOfWeek,
    now.getDay(),
    localTimeToMinutes(
      `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
    ),
  );
  const stateLabel = onAir ? t('dayparts.onAir') : t('dayparts.scheduled');
  const stateColor = onAir ? 'var(--gs-ok)' : 'var(--gs-muted)';

  return (
    <button
      type="button"
      onClick={() => onOpen(schedule.id)}
      className="flex w-full flex-wrap items-center gap-x-4 gap-y-3 border-b border-s-[3px] px-[22px] py-4 text-start last:border-b-0 hover:bg-[var(--gs-panel2)]"
      style={{
        borderBottomColor: 'var(--gs-line)',
        borderInlineStartColor: color.bar,
        color: 'var(--gs-ink)',
      }}
    >
      <span className="flex min-w-0 flex-[1_1_200px] flex-col gap-1">
        <span className="text-[15px] font-bold">
          {schedule.name?.trim() || t('untitledDaypart')}
        </span>
        <span className="text-[12.5px]" style={{ color: 'var(--gs-muted)' }}>
          {formatLocalWindow(schedule.startLocalTime, schedule.endLocalTime, 'range')} ·{' '}
          {formatDaysOfWeek(schedule.daysOfWeek)}
        </span>
      </span>

      <span className="flex min-w-[130px] flex-col gap-1">
        <span className="text-[13px]" style={{ color: 'var(--gs-ink2)' }}>
          {targetLabel}
        </span>
        <span className="text-xs" style={{ color: 'var(--gs-muted)' }}>
          {playlist}
        </span>
      </span>

      <span
        className="rounded-full border px-2.5 py-[5px] text-[11.5px] font-bold uppercase tracking-[0.08em]"
        style={{
          color: 'var(--gs-ink2)',
          background: 'var(--gs-panel2)',
          borderColor: 'var(--gs-line)',
        }}
      >
        {t('priorityBadge', { priority: schedule.priority })}
      </span>

      <span
        className="text-[11.5px] font-bold uppercase tracking-[0.08em]"
        style={{ color: stateColor }}
      >
        {stateLabel}
      </span>
    </button>
  );
}
